import React from "react";
import { defaultWorkspacePath } from "../utils/workspacePaths.mjs";
import { useLocation } from "react-router-dom";
import HeroSection from "../components/home/HeroSection";
import HomeProductOverview from "../components/home/HomeProductOverview";
import HomeHowItWorks from "../components/home/HomeHowItWorks";
import { acquisitionApi } from "../api/acquisition";
import { useAuth } from "../context/AuthContext";
import {
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionVisitorId,
  saveAcquisitionDraft,
} from "../utils/acquisitionFunnel";
import {
  PUBLIC_REGISTRATION_VERSION,
  publicRegistrationContext,
  publicRegistrationPath,
} from "../utils/publicRegistration";

// # HOME UX: one decision to start, content-driven height, light public entry.
// Scope ends at account creation; project setup stays in the existing dashboard.
export default function HomePage() {
  const { search } = useLocation();
  const { user, loading } = useAuth();
  const { referralCode, campaign, sessionId } = React.useMemo(
    () => publicRegistrationContext(search),
    [search],
  );
  const visitorId = React.useMemo(() => acquisitionVisitorId(), []);
  const [stats, setStats] = React.useState([]);

  React.useEffect(() => {
    let active = true;
    acquisitionApi
      .publicStats()
      .then((data) => {
        if (active) setStats(Array.isArray(data?.stats) ? data.stats : []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    const payload = {
      sessionId,
      visitorId,
      referralCode,
      campaign,
      entryPoint: "home",
      experienceVersion: PUBLIC_REGISTRATION_VERSION,
    };
    saveAcquisitionDraft(payload);
    acquisitionApi
      .startSession({ ...payload, audience: "unknown" })
      .catch(() => null);
    acquisitionApi
      .track({
        action: "acquisition_landing_view",
        sessionId,
        visitorId,
        eventId: `landing_${sessionId}_${PUBLIC_REGISTRATION_VERSION}`.slice(
          0,
          60,
        ),
        metadata: {
          stepKey: "landing",
          stepIndex: 0,
          totalSteps: 2,
          progressPct: 0,
          entryPoint: "home",
          audience: "unknown",
          experienceVersion: PUBLIC_REGISTRATION_VERSION,
          ...acquisitionDeviceMetadata(),
        },
      })
      .catch(() => null);
  }, [campaign, referralCode, sessionId, visitorId]);

  React.useEffect(() => {
    if (loading) return;
    acquisitionApi
      .startSession({
        sessionId,
        visitorId,
        referralCode,
        campaign,
        entryPoint: "home",
        audience: user ? "authenticated" : "anonymous",
        experienceVersion: PUBLIC_REGISTRATION_VERSION,
      })
      .catch(() => null);
  }, [campaign, loading, referralCode, sessionId, user, visitorId]);

  const trackStart = (entryPoint) => {
    if (user) return;
    // Telemetry never blocks a link or a registration request.
    acquisitionApi
      .track({
        action: "acquisition_cta_clicked",
        sessionId,
        visitorId,
        eventId: acquisitionEventId("home_cta"),
        metadata: {
          stepKey: "landing",
          stepIndex: 0,
          totalSteps: 2,
          progressPct: 0,
          entryPoint,
          audience: loading ? "unknown" : "anonymous",
          experienceVersion: PUBLIC_REGISTRATION_VERSION,
          ...acquisitionDeviceMetadata(),
        },
      })
      .catch(() => null);
  };

  return (
    <main className="wp-entry wp-home" id="contenido-home" tabIndex={-1}>
      <HeroSection
        stats={stats}
        startHref={
          user ? defaultWorkspacePath(user) : publicRegistrationPath(search, "home-hero")
        }
        onStartClick={() => trackStart("home-hero")}
        isAuthenticated={Boolean(user)}
        userName={user?.name || ""}
      />
      <HomeProductOverview />
      <HomeHowItWorks
        startHref={
          user ? defaultWorkspacePath(user) : publicRegistrationPath(search, "home-bottom")
        }
        onStart={() => trackStart("home-bottom")}
        isAuthenticated={Boolean(user)}
      />
    </main>
  );
}
